import React, { useEffect, useState } from 'react';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { WidgetSize } from '../../../types/Dashboard';
import { useAuth } from '../../../contexts/AuthContext';
import { firebaseService } from '../../../services/firebaseService';

interface AlertListWidgetProps {
  config: {
    alertType?: string;
  };
  size: WidgetSize;
}

const AlertListWidget: React.FC<AlertListWidgetProps> = ({ config, size }) => {
  const { userProfile } = useAuth();
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.empresaCnpj]);

  const loadAlerts = async () => {
    if (!userProfile?.empresaCnpj) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [turmas, mensalidades] = await Promise.all([
        firebaseService.getTurmas(userProfile.empresaCnpj),
        firebaseService.getAllMensalidades(userProfile.empresaCnpj)
      ]);

      const alertList: any[] = [];
      const hoje = new Date();

      turmas.filter(t => t.ativo).forEach(turma => {
        let professoresIds: string[] = [];
        if (typeof turma.professoresIds === 'string') {
          try { professoresIds = JSON.parse(turma.professoresIds); } catch {}
        } else if (Array.isArray(turma.professoresIds)) {
          professoresIds = turma.professoresIds;
        }
        if (professoresIds.length === 0) {
          alertList.push({
            id: `turma-${turma.id}`,
            type: 'warning',
            message: `Turma "${turma.nome}" sem professor atribuído`,
            icon: AlertTriangle
          });
        }
      });

      const vencidas = mensalidades.filter(m => {
        if (m.dataPagamento && m.dataPagamento !== '') return false;
        return new Date(m.dataVencimento) < hoje;
      });
      if (vencidas.length > 0) {
        alertList.push({
          id: 'mensalidades-vencidas',
          type: 'danger',
          message: `${vencidas.length} mensalidade(s) vencida(s)`,
          icon: AlertCircle
        });
      }

      turmas.filter(t => t.ativo).forEach(turma => {
        let alunosIds: string[] = [];
        if (typeof turma.alunosIds === 'string') {
          try { alunosIds = JSON.parse(turma.alunosIds); } catch {}
        } else if (Array.isArray(turma.alunosIds)) {
          alunosIds = turma.alunosIds;
        }
        const ocupacao = turma.limiteAlunos > 0 
          ? (alunosIds.length / turma.limiteAlunos) * 100 
          : 0;
        if (ocupacao < 50 && turma.limiteAlunos > 0) {
          alertList.push({
            id: `ocupacao-${turma.id}`,
            type: 'info',
            message: `Turma "${turma.nome}" com ${Math.round(ocupacao)}% de ocupação`,
            icon: Info
          });
        }
      });

      setAlerts(alertList);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  const maxItems = size === 'small' ? 3 : size === 'medium' ? 5 : 10;

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'danger': return 'bg-red-50 border-red-200 text-red-800';
      case 'warning': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'info': return 'bg-blue-50 border-blue-200 text-blue-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
        {alerts.slice(0, maxItems).map((alert) => {
          const Icon = alert.icon || AlertCircle;
          return (
            <div
              key={alert.id}
              className={`p-3 border rounded-lg ${getAlertColor(alert.type)}`}
            >
              <div className="flex items-start gap-2">
                <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-medium">{alert.message}</p>
              </div>
            </div>
          );
        })}
        {alerts.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            Nenhum alerta no momento
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertListWidget;

